import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { budgetService } from "~/services/contabilidad/budgetService";

import type {
  BudgetFilters,
  BudgetFormData,
  BudgetLineInput,
} from "~/types/contabilidad";

export const BUDGETS_QUERY_KEY = ["budgets"];
export const BUDGET_LINES_QUERY_KEY = ["budgetLines"];

export function useBudgets(filters?: BudgetFilters) {
  return useQuery({
    queryKey: [...BUDGETS_QUERY_KEY, filters],
    queryFn: () => budgetService.getAll(filters),
  });
}

export function useBudget(id: string) {
  return useQuery({
    queryKey: [...BUDGETS_QUERY_KEY, id],
    queryFn: () => budgetService.getById(id),
    enabled: !!id,
  });
}

export function useBudgetsByCompanyYear(companyId: string, fiscalYear: number) {
  return useQuery({
    queryKey: [...BUDGETS_QUERY_KEY, "byCompanyYear", companyId, fiscalYear],
    queryFn: () =>
      budgetService.getBudgetsByCompanyYear(companyId, fiscalYear),
    enabled: !!companyId && !!fiscalYear,
  });
}

export function useBudgetLines(budgetId: string) {
  return useQuery({
    queryKey: [...BUDGET_LINES_QUERY_KEY, budgetId],
    queryFn: () => budgetService.getLines(budgetId),
    enabled: !!budgetId,
  });
}

// Postgres unique_violation on budgets_company_year_name_unique (duplicate name
// within a company + year — several scenarios are allowed, but not same name).
function isDuplicateBudgetError(error: Error): boolean {
  return (
    "code" in error &&
    (error as { code?: string }).code === "23505"
  );
}

export function useCreateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BudgetFormData) => budgetService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto creado exitosamente");
    },
    onError: (error: Error) => {
      if (isDuplicateBudgetError(error)) {
        toast.error(
          "Ya existe un presupuesto con ese nombre para esta empresa y año"
        );
      } else {
        toast.error(`Error al crear presupuesto: ${error.message}`);
      }
    },
  });
}

export function useUpdateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BudgetFormData> }) =>
      budgetService.update(id, data),
    onSuccess: (updatedBudget, { id }) => {
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar presupuesto: ${error.message}`);
    },
  });
}

export function useApproveBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => budgetService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto aprobado");
    },
    onError: (error: Error) => {
      toast.error(`Error al aprobar presupuesto: ${error.message}`);
    },
  });
}

export function useReopenBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => budgetService.reopen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto reabierto como borrador");
    },
    onError: (error: Error) => {
      toast.error(`Error al reabrir presupuesto: ${error.message}`);
    },
  });
}

export function useDeleteBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => budgetService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto eliminado");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar presupuesto: ${error.message}`);
    },
  });
}

export function useSaveBudgetLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      budgetId,
      changedCells,
      removedAccountIds,
    }: {
      budgetId: string;
      changedCells: BudgetLineInput[];
      removedAccountIds: string[];
    }) => budgetService.saveLines(budgetId, changedCells, removedAccountIds),
    onSuccess: (_, { budgetId }) => {
      queryClient.invalidateQueries({
        queryKey: [...BUDGET_LINES_QUERY_KEY, budgetId],
      });
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto guardado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al guardar presupuesto: ${error.message}`);
    },
  });
}

export function useReplaceBudgetLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      budgetId,
      lines,
    }: {
      budgetId: string;
      lines: BudgetLineInput[];
    }) => budgetService.replaceLines(budgetId, lines),
    onSuccess: (_, { budgetId }) => {
      queryClient.invalidateQueries({
        queryKey: [...BUDGET_LINES_QUERY_KEY, budgetId],
      });
      queryClient.invalidateQueries({ queryKey: BUDGETS_QUERY_KEY });
      toast.success("Presupuesto importado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al importar presupuesto: ${error.message}`);
    },
  });
}
